import { ChangeDetectionStrategy, Component, effect, HostListener, inject, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatToolbarModule } from '@angular/material/toolbar';
import { RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { FaIconLibrary, FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { fas } from '@fortawesome/free-solid-svg-icons';
import { KEYCLOAK_EVENT_SIGNAL, KeycloakEventType } from 'keycloak-angular';
import Keycloak from 'keycloak-js';
import { environment } from '../environments/environment';
import { AuthService } from '@mottu-ops/sso';
import packageJson from '../../package.json';

@Component({
	selector: 'app-root',
	standalone: true,
	imports: [
		RouterOutlet,
		RouterLink,
		RouterLinkActive,
		MatSidenavModule,
		MatToolbarModule,
		MatButtonModule,
		MatIconModule,
		MatListModule,
		MatDividerModule,
		FontAwesomeModule,
	],
	templateUrl: './app.component.html',
	styleUrls: ['./app.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppComponent {
	title = 'SRE Portal';
	private keycloak = inject(Keycloak);
	private library = inject(FaIconLibrary);
	readonly authService = inject(AuthService);
	public version: string = packageJson.version;
	isMobile = signal<boolean>(window.innerWidth < 768);
	user = signal({
		name: '',
		email: '',
	});

	constructor() {
		this.library.addIconPacks(fas);

		const keycloakSignal = inject(KEYCLOAK_EVENT_SIGNAL);

		effect(async () => {
			const keycloakEvent = keycloakSignal();

			if (keycloakEvent.type === KeycloakEventType.Ready) {
				if (this.keycloak?.authenticated) {
					const profile = await this.keycloak.loadUserProfile();

					this.authService.credentials = {
						accessToken: this.keycloak.token || '',
					};

					this.user.set({
						name: `${profile?.firstName} ${profile.lastName}`,
						email: profile?.email || '',
					});

					this.authService.getUserInfo(environment.keycloak, this.keycloak.token);
				} else {
					await this.keycloak.login();
				}
			}

			if (
				keycloakEvent.type === KeycloakEventType.AuthLogout ||
				keycloakEvent.type === KeycloakEventType.AuthError ||
				keycloakEvent.type === KeycloakEventType.AuthRefreshError
			) {
				await this.keycloak.login();
			}
		});
	}

	logout() {
		this.keycloak.logout();
	}

	@HostListener('window:resize', ['$event'])
	onWindowResize() {
		this.isMobile.set(window.innerWidth < 768);
	}
}
